import { AdminMonthPanel } from '@/client/components/timesheets/AdminMonthPanel';
import { AdminTeamSidebar } from '@/client/components/timesheets/AdminTeamSidebar';
import { AdminWeekPanel } from '@/client/components/timesheets/AdminWeekPanel';
import { ExportDialog } from '@/client/components/timesheets/ExportDialog';
import { useAdminGuard } from '@/client/hooks/useAdminGuard';
import { getMonthRange, getWeekRange } from '@/client/lib/dates';
import { trpc } from '@/client/utils/trpc';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/_app/timesheets/admin')({
  component: AdminTimesheetsPage,
});

function AdminTimesheetsPage() {
  const { me, isLoading: meLoading } = useAdminGuard();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const [view, setView] = useState<'week' | 'month'>('week');
  const [exportOpen, setExportOpen] = useState(false);

  const weekRange = getWeekRange(weekOffset);
  const monthRange = getMonthRange(monthOffset);
  const sidebarRange = view === 'week' ? weekRange : monthRange;

  const { data: employees = [], isLoading } = trpc.timesheets.adminWeekStatus.useQuery(
    { date_from: sidebarRange.from, date_to: sidebarRange.to },
    { enabled: me?.role === 'ADMIN' },
  );

  if (meLoading || me?.role !== 'ADMIN') return null;

  const selectedEmployee = employees.find((e) => e.id === selectedEmployeeId) ?? null;
  const now = new Date();

  return (
    <div className="-mx-6 -my-6 flex flex-col h-full overflow-hidden">
      <div className="flex items-center gap-3 border-b px-4 py-3 shrink-0 sm:px-6">
        <div className="flex items-center gap-0.5 rounded-lg bg-muted p-0.5">
          {(['week', 'month'] as const).map((tab) => (
            <button
              type="button"
              key={tab}
              onClick={() => setView(tab)}
              className={`rounded-md px-3 py-1 text-xs font-medium capitalize transition-colors ${
                view === tab
                  ? 'bg-card text-foreground shadow-soft'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-1 flex-col overflow-hidden min-h-0 lg:flex-row">
        <AdminTeamSidebar
          employees={employees}
          selectedId={selectedEmployeeId}
          onSelect={setSelectedEmployeeId}
          onExport={() => setExportOpen(true)}
          isLoading={isLoading}
        />
        {view === 'week' ? (
          <AdminWeekPanel
            employee={selectedEmployee}
            weekOffset={weekOffset}
            onOffsetChange={setWeekOffset}
          />
        ) : (
          <AdminMonthPanel
            employee={selectedEmployee}
            monthOffset={monthOffset}
            onOffsetChange={setMonthOffset}
          />
        )}
      </div>
      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        defaultYear={now.getFullYear()}
        defaultMonth={now.getMonth() + 1}
      />
    </div>
  );
}
